import React, { useEffect, useState } from "react";
import { Drawer, Empty, Skeleton } from "antd";
import { useDispatch } from "react-redux";
import moment from "moment";
import styled from "styled-components";

import { convertToBRL, limitCharacterLength } from "~/utils/formatters";
import { loadProposals } from "../../actions";
import { necessity } from "../../constants";

var Container = styled.div`
  font-family: 'Roboto';
  user-select: none;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  position: relative;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
  background-color: #fff;
  cursor: pointer;
  margin-bottom: 12px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-color: #ffc107;
  }
`;

var Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

var Name = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #333;
`;

var Date = styled.div`
  font-size: 11px;
  color: #999;
  white-space: nowrap;
`;

var ProposalDate = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
  margin-top: 8px;
`;

var ProposalIcon = styled.span`
  color: #ffc107;
`;

var TagsRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

var Tag = styled.span`
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${function(props) { return props.bg || '#eee'; }};
  color: ${function(props) { return props.color || '#666'; }};
`;

var Honorary = styled.div`
  margin-top: 8px;
  font-weight: 600;
  color: #118632;
  font-size: 14px;
`;

var SkeletonContainer = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
`;

var DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

var TitleIcon = styled.span`
  font-size: 18px;
`;

var TitleText = styled.span`
  font-weight: 600;
`;

var PeriodBadge = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #666;
  background: #f5f5f5;
  padding: 2px 8px;
  border-radius: 4px;
`;

var stageColors = {
  1: { bg: '#6f42c1', name: 'Lead' },
  2: { bg: '#007bff', name: 'Oportunidade' },
  3: { bg: '#28a745', name: 'Positivo' },
  4: { bg: '#dc3545', name: 'Negativo' },
  5: { bg: '#ffc107', name: 'Follow Up' }
};

export var ProposalsDrawer = function(props) {
  var isVisible = props.isVisible;
  var onClose = props.onClose;
  var showDrawer = props.showDrawer;
  var month = props.month;
  var year = props.year;

  var dispatch = useDispatch();
  var loadingState = useState(false);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  var leadsState = useState([]);
  var leads = leadsState[0];
  var setLeads = leadsState[1];

  var metaState = useState(null);
  var meta = metaState[0];
  var setMeta = metaState[1];

  useEffect(function() {
    if (isVisible && month && year) {
      setIsLoading(true);
      dispatch(loadProposals({ month: month, year: year }))
        .then(function(response) {
          if (response && response.payload && response.payload.data && response.payload.data.data) {
            setLeads(response.payload.data.data.leads || []);
            setMeta(response.payload.data.data.meta || null);
          }
          setIsLoading(false);
        })
        .catch(function() {
          setIsLoading(false);
        });
    }
  }, [isVisible, month, year, dispatch]);

  var handleClose = function() {
    setLeads([]);
    setMeta(null);
    onClose();
  };

  var renderSkeleton = function() {
    return [1, 2, 3, 4, 5].map(function(i) {
      return React.createElement(SkeletonContainer, { key: i },
        React.createElement(Skeleton, { active: true, paragraph: { rows: 2 } })
      );
    });
  };

  var renderTitle = function() {
    return React.createElement(DrawerTitle, null,
      React.createElement(TitleIcon, null, "\u2709\uFE0F"),
      React.createElement(TitleText, null, "Propostas Enviadas"),
      meta && React.createElement(PeriodBadge, null, meta.period && meta.period.label)
    );
  };

  var renderLead = function(lead) {
    var stage = stageColors[lead.stage] || stageColors[1];
    var nec = necessity[lead.necessity];

    return React.createElement(Container, {
      key: lead.id,
      onClick: function() { showDrawer({ id: lead.id }); }
    },
      React.createElement('div', { style: { padding: '16px' } },
        React.createElement(Header, null,
          React.createElement(Name, null, limitCharacterLength(lead.name, 30)),
          React.createElement(Date, null, moment(lead.created_at).fromNow())
        ),
        React.createElement(ProposalDate, null,
          React.createElement(ProposalIcon, null, "\u2709"),
          "Proposta enviada em ",
          moment(lead.proposal_sent_at).format('DD/MM/YYYY')
        ),
        React.createElement(TagsRow, null,
          React.createElement(Tag, { bg: stage.bg, color: '#fff' }, stage.name),
          nec && React.createElement(Tag, { bg: nec.color, color: '#fff' }, nec.name)
        ),
        lead.honorary && !isNaN(parseFloat(lead.honorary)) && React.createElement(Honorary, null, convertToBRL(lead.honorary))
      )
    );
  };

  return React.createElement(Drawer, {
    title: renderTitle(),
    visible: isVisible,
    onClose: handleClose,
    width: 420,
    bodyStyle: { padding: '16px' }
  },
    isLoading && renderSkeleton(),
    !isLoading && leads.length === 0 && React.createElement(Empty, {
      description: "Nenhuma proposta enviada neste per\xEDodo."
    }),
    !isLoading && leads.length > 0 && React.createElement('div', null,
      React.createElement('div', {
        style: { marginBottom: '16px', color: '#666', fontSize: '13px' }
      }, meta && meta.total + " proposta(s) encontrada(s)"),
      leads.map(renderLead)
    )
  );
};

export default ProposalsDrawer;
